<template>
  <BaseCard type="outlined" class="w-full">
    <div class="p-4">
      <div class="flex flex-row">
        <div class="flex grow flex-col pr-2">
          <h3 class="text-headline-medium">Aktueller Dialog</h3>
          <span v-if="!asCoach" class="text-body-medium text-on-surface-variant mb-2"
            >mit <BaseMemberAvatar size="24" :member="participant" class="inline-flex!" />
            {{ memberFullName(participant) }}</span
          >
        </div>
        <BaseMenu v-if="menuItems.length" class="-mt-2 -mr-2" :items="menuItems" />
      </div>

      <CoacheeDialogTimeline :dialog="dialog" class="pt-4" />
    </div>
    <div
      v-if="asCoach && !dialog.plannedAt"
      class="bg-surface-container rounded-small text-body-medium mx-4 mb-1 flex items-center px-3 py-1"
    >
      <BaseIcon size="18" class="mr-2">today</BaseIcon>
      <span v-if="asCoach">{{ $t('dialog.set-date.title') }}</span>
      <BaseTextButton size="S" color="text" @click="openDialogDatePicker()" class="ml-auto">{{
        $t('dialog.set-date.action')
      }}</BaseTextButton>
    </div>

    <div
      v-if="dialog.phase != DialogPhase.Conducted"
      class="bg-surface-container rounded-small text-body-medium mx-4 flex items-center p-3"
    >
      <BaseIcon size="18" class="mr-2">{{
        (dialog.phase == DialogPhase.Feedback && asCoach) ||
        (dialog.phase == DialogPhase.CoachPreparation && !asCoach)
          ? 'hourglass'
          : 'play_arrow'
      }}</BaseIcon>
      <span v-if="dialog.phase == DialogPhase.Feedback && asCoach">{{
        $t('dialog.waiting-for-coachee', { firstName: memberShortName(dialog.coachee) })
      }}</span>
      <span v-if="dialog.phase == DialogPhase.CoachPreparation && !asCoach">{{
        $t('dialog.waiting-for-coach', { firstName: dialog.coach.firstName })
      }}</span>
      <span v-if="dialog.phase == DialogPhase.Feedback && !asCoach">{{
        $t('dialog.act-as-coachee')
      }}</span>
      <span v-if="dialog.phase == DialogPhase.CoachPreparation && asCoach">{{
        $t('dialog.act-as-coach')
      }}</span>
      <span v-if="dialog.phase == DialogPhase.Conversation && asCoach">{{
        $t('dialog.callToAction.coach.conversation')
      }}</span>
      <span v-if="dialog.phase == DialogPhase.Conversation && !asCoach">{{
        $t('dialog.callToAction.coachee.conversation')
      }}</span>
    </div>
    <div class="mt-2 flex flex-row justify-end gap-1 p-4">
      <BaseTextButton
        color="tonalPrimary"
        size="S"
        icon="arrow_forward"
        class="px-20"
        @click="routeToDialog"
        >Dialog öffnen</BaseTextButton
      >
    </div>
    <DialogDatePicker
      v-if="asCoach"
      :coachee="dialog.coachee"
      :dialog="dialog"
      :is-open="isOpenDialogDatePicker"
      @closeDialog="closeDialogDialogDatePicker"
      @dateSet="emits('dateSet')"
    />
  </BaseCard>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseCard from '@/core/components/base/card/BaseCard.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import { DialogPhase, type Dialog } from '@/core/models/dialog/dialog'
  import BaseMenu from '@/core/components/base/menu/BaseMenu.vue'
  import DialogDatePicker from '@/modules/teams/components/DialogDatePicker.vue'
  import type { SingleMenuItem } from '@/core/components/base/menu/MenuItem'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import { useRouter } from 'vue-router'
  import CoacheeDialogTimeline from './CoacheeDialogTimeline.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import { memberFullName, memberShortName } from '@/core/models/member/member-name'
  import { useI18n } from 'vue-i18n'

  interface Props {
    dialog: Dialog
    asCoach?: boolean
  }

  const props = defineProps<Props>()
  const router = useRouter()
  const { t } = useI18n()

  const emits = defineEmits<{
    (e: 'dateSet'): void
  }>()

  const routeToDialog = () => {
    router.push({ name: 'dialog', params: { id: props.dialog.id } })
  }

  const {
    openDialog: openDialogDatePicker,
    isOpen: isOpenDialogDatePicker,
    closeDialog: closeDialogDialogDatePicker,
  } = useDialog()

  const menuItems = computed<SingleMenuItem[]>(() => {
    const items: SingleMenuItem[] = []
    if (props.asCoach) {
      items.push({
        id: 'setDialogDate',
        leadingIcon: 'today',
        title: t('dialog.set-date.action'),
        callback: () => openDialogDatePicker(),
      })
    }
    return items
  })

  const participant = computed(() => {
    return props.asCoach ? props.dialog.coachee : props.dialog.coach
  })
</script>
